/**
 * Nuova scheda, modifica e nuova variante dalla base (V64), nello stesso modale.
 *
 * La nuova si apre dalla libreria (V63), modifica e variante dal dettaglio
 * (V65). La nuova e la variante, salvate, portano al loro dettaglio; la
 * modifica ricarica quello aperto. Il mix è raggruppato per famiglia, con
 * tutti gli attributi: una scheda la svolge chiunque. Il totale sta nel piede
 * del modale, sempre in vista, e il salvataggio è bloccato finché non è 100.
 */

import { type FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  creaSchedaSchema,
  ETICHETTA_TIPO_SCHEDA,
  TIPI_SCHEDA,
  type TipoScheda,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { EditorMix } from './EditorMix.js';
import { ICONA_TIPO_SCHEDA } from './icone.js';
import { mixDaQuote, type AttributoPerMix, type SchedaDettaglio } from './schede.js';

export function DialogScheda({
  ss,
  aperto,
  onChiudi,
  esistente,
  base,
  onSalvato,
}: {
  ss: string;
  aperto: boolean;
  onChiudi: () => void;
  /** La scheda da modificare. */
  esistente?: SchedaDettaglio;
  /** La base da cui nasce una variante, con il suo mix da ritoccare. */
  base?: SchedaDettaglio;
  /** Dopo la modifica; la nuova e la variante portano invece al loro dettaglio. */
  onSalvato?: () => Promise<void> | void;
}) {
  return (
    <Dialog open={aperto} onOpenChange={(a) => !a && onChiudi()}>
      {/* Campi e mix scorrono; intestazione e piede col totale restano fermi. */}
      <DialogContent className="flex max-h-[90dvh] flex-col sm:max-w-2xl">
        {aperto && (
          <Caricamento
            ss={ss}
            esistente={esistente}
            base={base}
            onChiudi={onChiudi}
            onSalvato={onSalvato}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Caricamento({
  ss,
  esistente,
  base,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  esistente?: SchedaDettaglio;
  base?: SchedaDettaglio;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const opzioni = useDati<{ attributi: AttributoPerMix[] }>('/schede/opzioni');
  if (!opzioni.dati) {
    return (
      <>
        <Intestazione esistente={esistente} base={base} />
        <StatoCaricamento errore={opzioni.errore} />
      </>
    );
  }
  return (
    <Form
      ss={ss}
      attributi={opzioni.dati.attributi}
      esistente={esistente}
      base={base}
      onChiudi={onChiudi}
      onSalvato={onSalvato}
    />
  );
}

function Intestazione({
  esistente,
  base,
}: {
  esistente?: SchedaDettaglio;
  base?: SchedaDettaglio;
}) {
  let titolo = 'Nuova scheda';
  let descrizione = 'Un esercizio della libreria: tipo, riferimenti e mix a somma 100.';
  if (esistente) {
    titolo = 'Modifica scheda';
    descrizione = esistente.nome;
  } else if (base) {
    titolo = 'Nuova variante';
    descrizione = `Parte dal mix di ${base.nome}: cambia il nome e ritocca quello che serve.`;
  }
  return (
    <DialogHeader>
      <DialogTitle>{titolo}</DialogTitle>
      <DialogDescription>{descrizione}</DialogDescription>
    </DialogHeader>
  );
}

function Form({
  ss,
  attributi,
  esistente,
  base,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  attributi: AttributoPerMix[];
  esistente?: SchedaDettaglio;
  base?: SchedaDettaglio;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const origine = esistente ?? base;
  const [nome, setNome] = useState(esistente?.nome ?? '');
  const [rpe, setRpe] = useState(String(origine?.rpeRiferimento ?? 5));
  const [minuti, setMinuti] = useState(String(origine?.minutiRiferimento ?? 10));
  const [tipo, setTipo] = useState<TipoScheda>(origine?.tipo ?? 'esercizio');
  const [quote, setQuote] = useState<Record<string, string>>(() =>
    Object.fromEntries((origine?.mix ?? []).map((v) => [v.attributoId, String(v.quota)])),
  );
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const { mix, totale } = useMemo(() => mixDaQuote(attributi, quote), [attributi, quote]);

  const salva = async () => {
    const esito = creaSchedaSchema.safeParse({
      nome,
      rpeRiferimento: Number(rpe),
      minutiRiferimento: Number(minuti),
      tipo,
      mix,
    });
    if (!esito.success) {
      setErrori(erroriPerCampo(esito.error));
      setErroreServer(esito.error.issues.find((i) => i.path[0] === 'mix')?.message ?? null);
      return;
    }
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      const { id } = await chiama<{ id: string }>(percorsoScrittura(esistente, base), {
        metodo: esistente ? 'PUT' : 'POST',
        corpo: esito.data,
      });
      toast.success(esistente ? 'Scheda salvata.' : 'Scheda creata.');
      if (esistente) {
        await onSalvato?.();
        onChiudi();
      } else {
        // La variante nasce sul dettaglio della base, che resta montato: il
        // modale si chiude prima di passare a quello nuovo.
        onChiudi();
        navigate(`/sq/${ss}/schede/${id}`);
      }
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const invia = (e: FormEvent) => {
    e.preventDefault();
    void salva();
  };

  return (
    <form onSubmit={invia} className="flex min-h-0 flex-1 flex-col gap-4">
      <Intestazione esistente={esistente} base={base} />

      <div className="-mx-4 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-1">
        <FieldGroup>
          <Field data-invalid={Boolean(errori.nome)}>
            <FieldLabel htmlFor="scheda-nome">Nome</FieldLabel>
            <Input
              id="scheda-nome"
              value={nome}
              aria-invalid={Boolean(errori.nome)}
              onChange={(e) => setNome(e.target.value)}
            />
            <FieldError>{errori.nome}</FieldError>
          </Field>

          <Field>
            <FieldLabel>Tipo</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              value={tipo}
              onValueChange={(v) => v && setTipo(v as TipoScheda)}
            >
              {TIPI_SCHEDA.map((t) => {
                const Icona = ICONA_TIPO_SCHEDA[t];
                return (
                  <ToggleGroupItem key={t} value={t}>
                    <Icona />
                    {ETICHETTA_TIPO_SCHEDA[t]}
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </Field>

          <div className="flex flex-col gap-5 sm:flex-row">
            <Field data-invalid={Boolean(errori.rpeRiferimento)}>
              <FieldLabel htmlFor="scheda-rpe">RPE di riferimento</FieldLabel>
              <Input
                id="scheda-rpe"
                type="number"
                min={1}
                max={10}
                value={rpe}
                aria-invalid={Boolean(errori.rpeRiferimento)}
                onChange={(e) => setRpe(e.target.value)}
              />
              <FieldError>{errori.rpeRiferimento}</FieldError>
            </Field>
            <Field data-invalid={Boolean(errori.minutiRiferimento)}>
              <FieldLabel htmlFor="scheda-minuti">Minuti di riferimento</FieldLabel>
              <Input
                id="scheda-minuti"
                type="number"
                min={1}
                value={minuti}
                aria-invalid={Boolean(errori.minutiRiferimento)}
                onChange={(e) => setMinuti(e.target.value)}
              />
              <FieldError>{errori.minutiRiferimento}</FieldError>
            </Field>
          </div>
        </FieldGroup>

        <EditorMix
          attributi={attributi}
          quote={quote}
          onQuota={(id, valore) => setQuote((q) => ({ ...q, [id]: valore }))}
        />
      </div>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      {/* Su telefono il totale sta sopra i pulsanti, non sotto come vorrebbe il piede. */}
      <DialogFooter className="flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <span
            className={cn(
              'shrink-0 font-medium tabular-nums',
              totale === 100 ? 'text-muted-foreground' : 'text-destructive',
            )}
          >
            Totale {totale} / 100
            {totale < 100 && <span className="font-normal"> · mancano {100 - totale}</span>}
            {totale > 100 && <span className="font-normal"> · {totale - 100} di troppo</span>}
          </span>
          <Progress
            value={Math.min(totale, 100)}
            aria-label="Totale del mix"
            className="sm:max-w-40"
          />
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={onChiudi}>
            Annulla
          </Button>
          <PulsanteInvio type="submit" inCorso={inCorso} disabled={totale !== 100}>
            Salva
          </PulsanteInvio>
        </div>
      </DialogFooter>
    </form>
  );
}

function percorsoScrittura(esistente?: SchedaDettaglio, base?: SchedaDettaglio) {
  if (esistente) return `/schede/${esistente.id}`;
  if (base) return `/schede/${base.id}/varianti`;
  return '/schede';
}
